import React from 'react';

interface GigaBallaLogoProps {
  className?: string;
  size?: number;
}

export const GigaBallaLogo: React.FC<GigaBallaLogoProps> = ({ className = '', size = 40 }) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 220 220"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`shrink-0 drop-shadow-xl ${className}`}
    >
      <defs>
        {/* Silver Metallic Bevel */}
        <linearGradient id="silverBevel" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#e2e8f0" />
          <stop offset="30%" stopColor="#94a3b8" />
          <stop offset="70%" stopColor="#f8fafc" />
          <stop offset="100%" stopColor="#475569" />
        </linearGradient>

        {/* Orange Accent Gradient */}
        <linearGradient id="orangeGlow" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ff7800" />
          <stop offset="50%" stopColor="#ea580c" />
          <stop offset="100%" stopColor="#9a3412" />
        </linearGradient>

        {/* Silver G Gradient */}
        <linearGradient id="silverG" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="50%" stopColor="#e2e8f0" />
          <stop offset="100%" stopColor="#94a3b8" />
        </linearGradient>

        {/* Orange B Gradient */}
        <linearGradient id="orangeB" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ff8a00" />
          <stop offset="60%" stopColor="#f97316" />
          <stop offset="100%" stopColor="#c2410c" />
        </linearGradient>

        {/* Ball Radial Shadow */}
        <radialGradient id="ballShade" cx="40%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#334155" />
          <stop offset="60%" stopColor="#0f172a" />
          <stop offset="100%" stopColor="#020617" />
        </radialGradient>
      </defs>

      {/* Top Center Star */}
      <polygon
        points="110,6 113,15 122,15 115,21 117,30 110,24 103,30 105,21 98,15 107,15"
        fill="#f97316"
        stroke="#ea580c"
        strokeWidth="1"
      />

      {/* Outer Shield Silver Frame */}
      <path
        d="M 110 18 L 192 42 C 192 128 152 182 110 206 C 68 182 28 128 28 42 L 110 18 Z"
        fill="url(#silverBevel)"
      />

      {/* Outer Shield Orange Inner Layer */}
      <path
        d="M 110 24 L 186 46 C 186 124 148 175 110 198 C 72 175 34 124 34 46 L 110 24 Z"
        fill="url(#orangeGlow)"
      />

      {/* Inner Black Shield Core */}
      <path
        d="M 110 30 L 180 50 C 180 120 144 168 110 190 C 76 168 40 120 40 50 L 110 30 Z"
        fill="#0a0a0f"
      />

      {/* Central Soccer Ball Base */}
      <circle cx="110" cy="115" r="46" fill="url(#ballShade)" stroke="#1e293b" strokeWidth="2" />

      {/* Soccer Ball Hexagon Mesh Texture Lines */}
      <path
        d="M 110 72 L 122 80 L 118 94 L 102 94 L 98 80 Z
           M 122 80 L 138 82 L 144 96 L 134 106 L 118 94
           M 98 80 L 82 82 L 76 96 L 86 106 L 102 94
           M 102 94 L 110 108 L 110 124 L 94 130 L 86 106
           M 118 94 L 110 108 L 110 124 L 126 130 L 134 106"
        fill="none"
        stroke="#334155"
        strokeWidth="1.5"
        opacity="0.5"
      />

      {/* Glowing Orange Ring swoosh surrounding the ball */}
      <path
        d="M 68 108 A 48 48 0 1 0 152 108 A 48 48 0 0 0 68 108"
        fill="none"
        stroke="url(#orangeGlow)"
        strokeWidth="4"
        strokeDasharray="220 40"
      />

      {/* Top Banner Black Ribbon with Arc */}
      <path
        d="M 32 50 Q 110 36 188 50 L 184 76 Q 110 62 36 76 Z"
        fill="#090d16"
        stroke="url(#orangeGlow)"
        strokeWidth="2.5"
      />

      {/* Inner Banner Silver Trim Lines */}
      <path
        d="M 38 54 Q 110 42 182 54"
        fill="none"
        stroke="#cbd5e1"
        strokeWidth="1"
        opacity="0.7"
      />

      {/* "GIGABALLS" Banner Text */}
      <text
        x="110"
        y="68"
        fill="#ffffff"
        fontSize="16"
        fontWeight="900"
        fontFamily="Arial, sans-serif"
        textAnchor="middle"
        letterSpacing="2.5"
      >
        GIGABALLS
      </text>

      {/* Central "GB" Monogram */}
      <g transform="translate(68, 80)">
        {/* 'G' - Silver 3D italic styling */}
        <text
          x="0"
          y="48"
          fill="url(#silverG)"
          fontSize="48"
          fontWeight="900"
          fontFamily="Arial, sans-serif"
          fontStyle="italic"
          stroke="#0f172a"
          strokeWidth="2"
        >
          G
        </text>

        {/* Top Blade extension over 'G' */}
        <path
          d="M 12 8 Q 28 -2 46 4 L 38 10 Q 24 6 14 12 Z"
          fill="url(#silverG)"
        />

        {/* 'B' - Orange 3D italic styling */}
        <text
          x="38"
          y="48"
          fill="url(#orangeB)"
          fontSize="48"
          fontWeight="900"
          fontFamily="Arial, sans-serif"
          fontStyle="italic"
          stroke="#7c2d12"
          strokeWidth="2"
        >
          B
        </text>
      </g>

      {/* Bottom Stars & "FC" Section */}
      <g transform="translate(110, 172)">
        {/* Left Star */}
        <polygon points="-32,-4 -29,2 -22,2 -27,6 -25,12 -32,8 -39,12 -37,6 -42,2 -35,2" fill="#f97316" />
        
        {/* "FC" Text */}
        <text
          x="0"
          y="4"
          fill="#f8fafc"
          fontSize="14"
          fontWeight="900"
          fontFamily="Arial, sans-serif"
          textAnchor="middle"
          letterSpacing="3"
        >
          FC
        </text>

        {/* Right Star */}
        <polygon points="32,-4 35,2 42,2 37,6 39,12 32,8 25,12 27,6 22,2 29,2" fill="#f97316" />

        {/* Bottom Diamond */}
        <polygon points="0,12 6,17 0,22 -6,17" fill="#f97316" />
      </g>
    </svg>
  );
};

